import { components } from "@/lib/ApiSchema";
import PartThumbnail from "@/lib/PartThumbnail";
import ClearIcon from "~icons/fa6-solid/x";
import useModal from "@/lib/modal/useModal";
import Online3DViewer from "@/lib/Online3DViewer";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import Button from "@/lib/Button";
import ConfirmationModal from "@/lib/modal/ConfirmationModal";
import PartInput from "./PartInput";
import PartStatusIcon from "@/lib/PartStatusIcon";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import SelectCadModel from "./SelectCadModel";
import SelectTechnicalDrawing from "./SelectTechnicalDrawing";
import $api from "@/lib/queries/openapi";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import classnames from "classnames";

function sameQuantities(a: number[], b: number[]) {
  return a.length === b.length && a.every((v, i) => b[i] === v);
}

function inputToQuantities(input: string | undefined) {
  return (
    input
      ?.split(",")
      .map((q) => parseInt(q.trim(), 10))
      .filter((q) => !Number.isNaN(q)) ?? []
  );
}

function getPartBadge(
  part: components["schemas"]["RequestedPartDetailSchema"],
) {
  if (part.operations.length === 0 || part.operations.length > 1)
    return undefined;

  const operation = part.operations[0];
  if (part.treat_as_ots) {
    return "Purchased";
  } else if (operation.type === "ExpenseNonRecurring") {
    return "Other expense";
  }

  return undefined;
}

export type PendingSavePayload =
  components["schemas"]["RequestedPartCreateSchemaPatch"];

type Props = {
  part: components["schemas"]["RequestedPartDetailSchema"];
  setInputFocusing: Dispatch<SetStateAction<boolean>>;
  canEdit: boolean;
  onCreated: () => void;
  newPartId: string;
  inverseDepth: number;
  indentationWidth: number;
};

export default function PartTreeItem({
  part,
  setInputFocusing,
  canEdit,
  onCreated,
  newPartId,
  inverseDepth,
  indentationWidth,
}: Props) {
  const { showModal, closeModal } = useModal();
  const { updatePart } = useUpdatePart();
  const [pendingSave, setPendingSave] = useState<PendingSavePayload>();
  const quoteRevision = useGetCurrentQuoteRevision();

  const isCreatingNewPart =
    pendingSave?.part_number &&
    pendingSave?.requested_quantities &&
    part.id === newPartId &&
    quoteRevision;

  const truncateThreshold = 22;

  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  const magicThreshold = 1400 - inverseDepth * indentationWidth;

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex items-center justify-between text-sm grow">
      <div className="flex">
        {part.parent ? (
          <div className="flex flex-col self-center shrink-0">
            <div className="flex items-center">
              <PartInput
                className="w-6 text-xs"
                type="number"
                disabled={!canEdit}
                min={1}
                initialInput={
                  pendingSave?.qty_for_parent?.toString() ??
                  part.qty_for_parent?.toString() ??
                  ""
                }
                setInputFocusing={setInputFocusing}
                save={(newValue) => {
                  const qtyForParent = parseInt(newValue ?? "", 10);
                  if (
                    !Number.isNaN(qtyForParent) &&
                    qtyForParent > 0 &&
                    qtyForParent !== part.qty_for_parent
                  ) {
                    setPendingSave((pre) => {
                      return {
                        ...pre,
                        qty_for_parent: qtyForParent,
                      };
                    });
                  }
                }}
              />
              <ClearIcon className="text-slate-400 w-3 h-2.5 mx-1" />
            </div>
            {pendingSave &&
              pendingSave.qty_for_parent &&
              part.id !== newPartId && (
                <SaveCancel
                  className="mt-1 mr-2"
                  part={part}
                  pendingSave={pendingSave}
                  setPendingSave={setPendingSave}
                  quoteRevisionId={""}
                />
              )}
          </div>
        ) : (
          // has to match the width above
          <div className="w-8" />
        )}
        <div className="relative flex">
          <PartStatusIcon
            willBid={part.will_bid}
            className="absolute top-[-.25rem] left-12 bg-blue-100 rounded-full p-1 z-10"
          />
          <PartThumbnail
            className="border-[0.8px] border-slate-200 size-15 rounded-xs"
            url={part.thumbnail_url}
            badgeText={getPartBadge(part)}
            onClick={(e) => {
              if (!part.cad_model?.embed_url) return;

              e.preventDefault();
              showModal(
                <div className="w-[calc(100vw-100px)] h-[calc(100vh-100px)] flex flex-col">
                  <div className="w-full flex justify-end p-4">
                    <ClearIcon
                      className="size-4 cursor-pointer text-slate-400 hover:text-slate-600"
                      onClick={closeModal}
                    />
                  </div>
                  <Online3DViewer
                    className="grow w-full"
                    fileUrl={part.cad_model.embed_url}
                  />
                </div>,
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-1 ml-2">
          <div className="flex gap-2 items-center">
            <PartInput
              className={classnames(
                "min-w-32 field-sizing-content truncate pr-4",
                {
                  "max-w-44": windowWidth - magicThreshold <= 0,
                },
              )}
              // w-44 = 11rem = 176px
              style={{
                maxWidth: `${windowWidth - magicThreshold}px`,
              }}
              initialInput={part.part_number}
              disabled={!canEdit}
              setInputFocusing={setInputFocusing}
              required={part.id === newPartId}
              autoFocus={part.id === newPartId}
              save={(newValue) => {
                // part number is required, so we don't allow it to be empty
                if (part.id === newPartId && newValue) {
                  setPendingSave((pre) => {
                    return {
                      ...pre,
                      part_number: newValue,
                    };
                  });
                } else if (newValue && newValue !== part.part_number) {
                  updatePart({
                    payload: { part_number: newValue },
                    partId: part.id,
                  });
                }
              }}
            />
            <span className="text-slate-500">Rev</span>
            <PartInput
              className="min-w-9 max-w-14 pr-4 field-sizing-content truncate"
              initialInput={part.revision_number ?? ""}
              disabled={!canEdit}
              setInputFocusing={setInputFocusing}
              save={(newValue) => {
                if (part.id === newPartId && newValue) {
                  setPendingSave((pre) => {
                    return {
                      ...pre,
                      revision_number: newValue,
                    };
                  });
                } else if (newValue !== part.revision_number) {
                  updatePart({
                    payload: { revision_number: newValue || null },
                    partId: part.id,
                  });
                }
              }}
            />
          </div>
          <PartInput
            className="text-slate-500 truncate"
            initialInput={part.description ?? ""}
            disabled={!canEdit}
            placeholder="No description"
            setInputFocusing={setInputFocusing}
            save={(newValue) => {
              if (part.id === newPartId && newValue) {
                setPendingSave((pre) => {
                  return {
                    ...pre,
                    description: newValue,
                  };
                });
              } else if (newValue !== part.description) {
                updatePart({
                  payload: { description: newValue || null },
                  partId: part.id,
                });
              }
            }}
          />
        </div>
      </div>

      <div className="flex">
        {/* ensures there's always a direct vertical line for the indentation */}
        {Array.from({ length: inverseDepth }).map((_, i) => (
          <div key={i} className={`w-[${indentationWidth}px]`} />
        ))}

        <div className="flex flex-col mx-8">
          <PartInput
            initialInput={
              pendingSave?.requested_quantities?.join(",") ??
              part.requested_quantities.join(",") ??
              ""
            }
            disabled={!!part.parent || !canEdit}
            className={`min-w-28 truncate pr-4 ${part.parent ? "!bg-transparent" : ""}`}
            setInputFocusing={setInputFocusing}
            required={part.id === newPartId}
            save={(newValue) => {
              const newQuantities = inputToQuantities(newValue ?? "");
              const currentQuantities = inputToQuantities(
                part.requested_quantities.join(",") ?? "",
              );

              if (!sameQuantities(newQuantities, currentQuantities)) {
                setPendingSave((pre) => {
                  return {
                    ...pre,
                    requested_quantities: newQuantities,
                  };
                });
              }
            }}
          />

          {pendingSave &&
            Array.isArray(pendingSave.requested_quantities) &&
            part.id !== newPartId && (
              <SaveCancel
                className="mt-1"
                part={part}
                pendingSave={pendingSave}
                setPendingSave={setPendingSave}
                quoteRevisionId={""}
              />
            )}
        </div>

        <div className="w-52 mr-4">
          <SelectTechnicalDrawing
            disabled={!canEdit}
            pendingSave={pendingSave}
            technicalDrawing={part.technical_drawing}
            technicalDrawings={quoteRevision?.technical_drawings ?? []}
            setPendingSave={setPendingSave}
            truncateThreshold={truncateThreshold}
          />
          {pendingSave &&
            pendingSave.technical_drawing_id &&
            part.id !== newPartId && (
              <SaveCancel
                className="mt-1"
                part={part}
                pendingSave={pendingSave}
                setPendingSave={setPendingSave}
                quoteRevisionId={""}
              />
            )}
        </div>

        <div className="w-52">
          <SelectCadModel
            disabled={!canEdit}
            cadModel={part.cad_model}
            cadModels={quoteRevision?.cad_models ?? []}
            pendingSave={pendingSave}
            setPendingSave={setPendingSave}
            truncateThreshold={truncateThreshold}
          />
          {pendingSave && pendingSave.cad_model_id && part.id !== newPartId && (
            <SaveCancel
              className="mt-1"
              part={part}
              pendingSave={pendingSave}
              setPendingSave={setPendingSave}
              quoteRevisionId={""}
            />
          )}
        </div>

        {isCreatingNewPart && (
          <SaveCancel
            className="ml-2"
            part={part}
            pendingSave={pendingSave}
            setPendingSave={setPendingSave}
            quoteRevisionId={quoteRevision.id}
            onCreated={onCreated}
            newPartId={newPartId}
          />
        )}
      </div>
    </div>
  );
}

type SaveCancelProps = {
  part: Props["part"];
  className?: string;
  pendingSave: PendingSavePayload;
  setPendingSave: Dispatch<SetStateAction<PendingSavePayload | undefined>>;
  quoteRevisionId: string;
  onCreated?: () => void;
  newPartId?: string;
};

function SaveCancel({
  part,
  pendingSave,
  setPendingSave,
  quoteRevisionId,
  onCreated,
  newPartId,
  className,
}: SaveCancelProps) {
  const { showModal, closeModal } = useModal();
  const { updatePart, isPending } = useUpdatePart();

  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate: createPart, isPending: isPendingCreatePart } =
    $api.useMutation("post", "/api/teams/{team_slug}/requested_parts");

  return (
    <div className={classnames("flex gap-1", className)}>
      <Button
        primary
        size="sm"
        loading={isPending || isPendingCreatePart}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (newPartId && part.id === newPartId) {
            createPart(
              {
                params: {
                  path: {
                    team_slug: teamSlug!,
                  },
                },
                body: {
                  ...pendingSave,
                  part_number: pendingSave.part_number || "",
                  requested_quantities: pendingSave.requested_quantities || [],
                  quote_revision_id: quoteRevisionId,
                  note: part.note,
                  ordinal: part.ordinal,
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
                  });
                  queryClient.invalidateQueries({
                    queryKey: [
                      "get",
                      "/api/teams/{team_slug}/requested_parts/find_with_operations",
                    ],
                  });
                  setPendingSave(undefined);
                  onCreated?.();
                },
              },
            );
          } else if (
            pendingSave.cad_model_id ||
            pendingSave.cad_model_node_id ||
            pendingSave.technical_drawing_id
          ) {
            showModal(
              <ConfirmationModal
                onCancel={() => {
                  closeModal();
                  setPendingSave(undefined);
                }}
                onConfirm={() => {
                  updatePart({
                    payload: pendingSave,
                    partId: part.id,
                    onSuccess: () => {
                      setPendingSave(undefined);
                    },
                  });
                  closeModal();
                }}
              >
                Are you sure you want to reprocess this part estimate with new
                files?
              </ConfirmationModal>,
            );
          } else {
            updatePart({
              payload: pendingSave,
              partId: part.id,
              onSuccess: () => {
                setPendingSave(undefined);
              },
            });
          }
        }}
      >
        Save
      </Button>
      <Button
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setPendingSave(undefined);
        }}
      >
        Cancel
      </Button>
    </div>
  );
}
